import { Button } from "antd";
import clsx from "clsx";
import { useState } from "react";
import ModalWithForm, { FieldProps } from "src/components/ModalWithForm";
import { Product } from "src/models/ProductModel";
import { useUpdateProductMutation } from "src/store/apis/Products";
import { useGetTagsQuery } from "src/store/apis/Tags";

interface Props {
  record: Product | null;
}

export default function EditModal({ record }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormDisabled, setIsFormDisabled] = useState(true);

  const {
    data: tags,
    isLoading: isLoadingTags,
    isFetching: isFetchingTags,
  } = useGetTagsQuery();

  const [updateProduct, { isLoading: isLoadingUpdate }] =
    useUpdateProductMutation();

  async function handleConfirm(
    /*Necessary because offer tags typing is inconsistent*/
    formData: Omit<Product, "offerTags"> & {
      offerTags: number[];
    },
  ) {
    if (!record) return;

    try {
      await updateProduct({
        data: {
          offerNumber: record.offerNumber,
          sortingNumber:
            Number(formData.sortingNumber) ?? Number(record.sortingNumber),
          provider: formData.provider ?? record.provider,
          isFrontend: formData.isFrontend,
          offerTags: formData.offerTags.map((tagId) => ({
            tagId: Number(tagId),
            tagNameEn:
              tags?.result?.data.find((tag) => tag.id === tagId)?.name ?? "",
            tagNameAr:
              tags?.result?.data.find((tag) => tag.id === tagId)?.nameAr ?? "",
          })),
        },
      }).then(() => {
        setIsModalOpen(false);
        setIsFormDisabled(true);
      });
    } catch (error) {
      // TODO: implement toaster
      console.error(error);
    }
  }

  const fields: FieldProps<Product>[] = [
    {
      label: "Provider",
      name: "provider",
      type: "text",
      inputProps: {
        name: "provider",
        disabled: true,
      },
    },
    {
      label: "Offer Tags",
      name: "offerTags",
      type: "select",
      selectProps: {
        mode: "tags",
        disabled: isFormDisabled,
        loading: isLoadingTags || isFetchingTags,
        options: tags?.result.data.map((tag) => ({
          label: tag.name,
          value: tag.id,
        })),
      },
    },
    {
      label: "Is Frontend",
      name: "isFrontend",
      type: "switch",
      switchProps: {
        disabled: isFormDisabled,
      },
    },
    {
      label: "Sorting Number",
      name: "sortingNumber",
      type: "text",
      inputProps: {
        name: "sortingNumber",
        disabled: isFormDisabled,
      },
    },
  ];

  return (
    <>
      <div className="flex justify-between gap-4">
        <Button
          className={clsx(
            "rounded-sm text-12 border-0 font-bold uppercase bg-primary text-white disabled:bg-gray-500",
          )}
          onClick={() => setIsModalOpen(true)}
          disabled={!record}
        >
          Edit
        </Button>
      </div>
      <ModalWithForm
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        afterClose={() => {
          setIsFormDisabled(true);
        }}
        onConfirm={handleConfirm}
        isLoading={isLoadingUpdate}
        mode="edit"
        isEditing={!isFormDisabled}
        onEdit={() => setIsFormDisabled((prev) => !prev)}
        title={
          <>
            <h3>{record?.offerNameEn}</h3>
            <p className="font-normal text-sm text-light">{record?.offerId}</p>
          </>
        }
        initialValues={{
          offerNumber: record?.offerNumber,
          offerNameEn: record?.offerNameEn,
          offerTags: record?.offerTags.map((tag) => tag.tagId),
          provider: record?.provider,
          sortingNumber: record?.sortingNumber,
          isFrontend: record?.isFrontend,
        }}
        fields={fields}
      />
    </>
  );
}
